// core/audio-engine.js — HTMLAudioElement wrapper. The IAudioPlayer of the
// web port.
//
// This is the platform-specific player that the pure-state-machine
// `engines/audio-engine.js` consumes via dependency injection. It mirrors
// `Assets/_App/Platform/Unity/UnityAudioPlayer.cs` from the Unity port.
//
// Responsibilities (matching the Unity port's IAudioPlayer interface):
//   • Load(filePath) — set the audio source
//   • Play() / Pause() / Stop() / SeekTo(s) — transport controls
//   • CurrentTimeSec / DurationSec / IsPlaying — getters
//   • OnTimeUpdated — fires on `timeupdate` (~4 Hz, browser-decided)
//   • OnPlaybackEnded — fires on natural end
//   • OnError — fires on load or playback error
//
// iOS Safari notes:
//   • The first play() must be called inside a user gesture; we don't try to
//     work around that here, the calling screen is responsible.
//   • playbackRate works on iOS for HTMLAudioElement.

export class BrowserAudioPlayer extends EventTarget {
  constructor() {
    super();
    this.el = new Audio();
    this.el.preload = "auto";
    this.el.crossOrigin = "anonymous";
    this.el.addEventListener("timeupdate", () => {
      this.dispatchEvent(new CustomEvent("timeUpdated", { detail: this.el.currentTime }));
    });
    this.el.addEventListener("ended", () => {
      this.dispatchEvent(new Event("playbackEnded"));
    });
    this.el.addEventListener("error", () => {
      this.dispatchEvent(new CustomEvent("error", { detail: this.el.error?.message || "audio error" }));
    });
  }

  // ── Load / Unload ────────────────────────────────────────────────────────

  /**
   * Load a single URL onto the underlying `<audio>` element.
   * Used for one-source items (and unit tests). The player normally calls
   * `loadSources` so the browser can fall back through formats.
   *
   * @param {string} filePath
   */
  load(filePath) {
    this._clearSources();
    this.el.src = filePath;
    this.el.currentTime = 0;
    this.el.load();
  }

  /**
   * Load an ordered list of `<source>` candidates so the browser picks the
   * first format it can decode. Brief 002 introduced this so the player can
   * serve `.m4a` (master, AAC) to iOS Safari and Chromium and fall back to
   * `.ogg` (Vorbis) on Firefox-Linux without AAC codecs.
   *
   * Order matters: the first decodable URL wins. The caller must order
   * highest-quality / widest-support first.
   *
   * @param {Array<{url: string, type: string}>} sources
   */
  loadSources(sources) {
    this._clearSources();
    this.el.removeAttribute("src");
    for (const s of sources) {
      const el = document.createElement("source");
      el.src = s.url;
      el.type = s.type;
      this.el.appendChild(el);
    }
    this.el.currentTime = 0;
    // Browsers don't always re-evaluate <source> children when you swap them
    // in — explicitly re-trigger format selection.
    this.el.load();
  }

  unload() {
    this.el.pause();
    this._clearSources();
    this.el.removeAttribute("src");
    this.el.load();
  }

  _clearSources() {
    for (const child of [...this.el.querySelectorAll("source")]) {
      child.remove();
    }
  }

  // ── Transport ────────────────────────────────────────────────────────────

  /** @returns {Promise<void>} resolves when play() is accepted (or rejected by Safari) */
  play() { return this.el.play(); }
  pause() { this.el.pause(); }
  stop() { this.el.pause(); this.el.currentTime = 0; }
  /** @param {number} sec */
  seekTo(sec) { this.el.currentTime = Math.max(0, sec); }

  // ── Getters ──────────────────────────────────────────────────────────────

  get currentTimeSec() { return this.el.currentTime; }
  get durationSec() { return Number.isFinite(this.el.duration) ? this.el.duration : 0; }
  get isPlaying() { return !this.el.paused && !this.el.ended; }
}

/** Module-level singleton, available to all engines/screens via PlatformServices. */
export const audioPlayer = new BrowserAudioPlayer();
